import { bildMarker, bildPlatzhalter, hydratisiereBilder, pruefeNotizbild } from "./bildDarstellung";
import { hinweisen } from "../redesign/bestaetigen";
import { BILD_SRC } from "./blockHtml";
import { aktiveAuswahl, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

/**
 * Einsetzen an der Auswahl (PO 14.09.2026: „bilder in die notiz laden, links
 * wie in Notion") — Bild, Link, Inhalt der Zwischenablage.
 *
 * Bewusst OHNE React und OHNE `execCommand`, wie `bezugEinsetzen.ts`
 * daneben: im contentEditable ist das DOM die Quelle der Wahrheit, und die
 * Range-Arithmetik ist der Teil, der schiefgehen kann. Als freie Funktionen
 * ist sie im jsdom prüfbar, ohne eine Komponente zu montieren — `execCommand`
 * gibt es dort gar nicht.
 */

/**
 * Genau EINE URL, sonst nichts — so sieht die Zwischenablage aus, wenn
 * jemand eine Adresse aus der Browserzeile kopiert hat. Ein `www.`-Anfang
 * wird zu `https://` ergänzt. Text mit einer URL DARIN bleibt Text: der
 * Absatz aus einer Mail soll nicht plötzlich ein einziger Link sein.
 */
export function urlAusText(text: string | null | undefined): string | null {
    const t = (text ?? "").trim();
    if (!t || /\s/.test(t)) return null;
    if (/^https?:\/\/[^\s<>"']+$/i.test(t)) return t;
    if (/^www\.[^\s<>"']+\.[a-z]{2,}(?:[/?#][^\s<>"']*)?$/i.test(t)) return `https://${t}`;
    return null;
}

/** Ein Link, wie ihn der Reiniger beim Speichern ohnehin erzwingt. */
export function linkElement(url: string, text: string): HTMLAnchorElement {
    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
    a.textContent = text;
    return a;
}

/** Liegt die Auswahl (ganz) in diesem Feld? */
function auswahlImFeld(feld: HTMLElement, sel: Selection | null): Range | null {
    if (!sel || sel.rangeCount === 0) return null;
    const r = sel.getRangeAt(0);
    return feld.contains(r.commonAncestorContainer) ? r : null;
}

/**
 * Den Cursor HINTER ein eingesetztes Element setzen, in einen eigenen
 * Textknoten — dieselbe Lücke wie hinter einem `#`-Bezug: auf einer
 * Elementgrenze schlagen Browser den Caret dem Element davor zu, und der
 * nächste Anschlag landete im Link.
 */
function cursorDahinter(sel: Selection | null, el: Node): void {
    const luecke = document.createTextNode("\u00A0");
    (el as ChildNode).after(luecke);
    if (!sel) return;
    const r = aktivesDokument().createRange();
    r.setStart(luecke, 1);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
}

/**
 * Ein Element an der Auswahl einsetzen: ersetzt Markiertes, sonst steht es
 * am Caret. Liegt die Auswahl NICHT im Feld (der Upload lief, und der Nutzer
 * hat inzwischen woanders geklickt), kommt es ans Ende — verloren gehen darf
 * ein hochgeladenes Bild nicht.
 */
export function einsetzenAnAuswahl(feld: HTMLElement, sel: Selection | null, el: Node): void {
    const r = auswahlImFeld(feld, sel);
    if (r) {
        r.deleteContents();
        r.insertNode(el);
    } else {
        feld.appendChild(el);
    }
    cursorDahinter(sel, el);
}

/**
 * Ein Element durch ein anderes ersetzen — der Upload-Platzhalter wird zum
 * Bild. Der Cursor bleibt, wo er ist: der Nutzer schreibt womöglich schon
 * weiter.
 */
export function ersetzen(alt: Element, neu: Node): void {
    alt.replaceWith(neu);
}

/**
 * Einen Link einsetzen. Ist Text markiert, wird ER zum Linktext („wie in
 * Notion"); sonst steht die Adresse selbst da.
 *
 * `extractContents` statt `surroundContents`: Letzteres scheitert, sobald
 * die Markierung über eine Elementgrenze läuft (halb fett, halb nicht).
 */
export function linkEinsetzen(feld: HTMLElement, sel: Selection | null, url: string): HTMLAnchorElement {
    const r = auswahlImFeld(feld, sel);
    if (r && !r.collapsed) {
        const inhalt = r.extractContents();
        const a = linkElement(url, "");
        a.textContent = "";
        a.appendChild(inhalt);
        // Ein Link im Link ergibt keinen Sinn — der äußere gewinnt.
        for (const innen of Array.from(a.querySelectorAll("a"))) {
            innen.replaceWith(...Array.from(innen.childNodes));
        }
        if (!a.textContent?.trim()) a.textContent = url;
        r.insertNode(a);
        cursorDahinter(sel, a);
        return a;
    }
    const a = linkElement(url, url);
    einsetzenAnAuswahl(feld, sel, a);
    return a;
}

/**
 * Den Cursor an die Stelle setzen, an der etwas abgelegt wurde. Ohne das
 * landete ein gezogenes Bild dort, wo der Cursor ZULETZT stand — oder, wenn
 * das Feld nie fokussiert war, ganz am Ende, weit weg von der Maus.
 * Liefert `true`, wenn die Stelle im Feld liegt.
 */
export function auswahlAnPunkt(feld: HTMLElement, x: number, y: number): boolean {
    const doc = feld.ownerDocument;
    let r: Range | null = null;
    const mitRange = doc as Document & { caretRangeFromPoint?: (x: number, y: number) => Range | null };
    const mitPos = doc as Document & {
        caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    };
    if (typeof mitPos.caretPositionFromPoint === "function") {
        const pos = mitPos.caretPositionFromPoint(x, y);
        if (pos) {
            r = doc.createRange();
            r.setStart(pos.offsetNode, pos.offset);
        }
    } else if (typeof mitRange.caretRangeFromPoint === "function") {
        r = mitRange.caretRangeFromPoint(x, y);
    }
    if (!r || !feld.contains(r.startContainer)) return false;
    r.collapse(true);
    const sel = doc.defaultView?.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(r);
    return true;
}

/** Die Bilddateien aus einer Zwischenablage oder einem Drop; alles andere bleibt liegen. */
export function bildDateienAus(dt: DataTransfer | null | undefined): File[] {
    return Array.from(dt?.files ?? []).filter((f) => f.type.startsWith("image/"));
}

/**
 * Die Bilddateien, die beim EINFÜGEN hochgeladen werden sollen.
 *
 * Kopierte Notizen, Mails oder Webseiten mit Bildern bringen neben dem HTML
 * oft auch eine Bilddatei mit (User 03.11.2026: „wenn man Notizen mit Bildern
 * pastet, dann einfach pasten"). Steht im HTML Text, gewinnt das HTML: Eigene
 * Notizbilder (`data-bild-id`) und `https`-Bilder darin bleiben erhalten, die
 * Datei wird NICHT hochgeladen — sonst ginge der Text verloren und ein
 * gescheiterter Upload meldete sich mit einem Popup. Nur ein reines Bild
 * (kein Text im HTML) geht in den Upload.
 */
export function einfuegeBildDateien(dt: DataTransfer | null | undefined): File[] {
    const dateien = bildDateienAus(dt);
    if (!dateien.length) return dateien;
    const html = dt?.getData("text/html");
    if (!html) return dateien;
    const topf = document.createElement("div");
    topf.innerHTML = html;
    for (const el of Array.from(topf.querySelectorAll("style,script,meta,link,title,xml"))) el.remove();
    return (topf.textContent ?? "").trim() ? [] : dateien;
}

/**
 * Die Quellen der Bilder im eingefügten HTML, die der Reiniger verwerfen
 * würde — in Dokumentreihenfolge. Das sind Bilder ohne eigenen Marker und
 * ohne erlaubte Quelle: eingebettet (`data:`), aus dem Browser (`blob:`),
 * von der Platte (`file:`) oder aus einer Mail (`cid:`). Sie gehen sonst
 * beim Einfügen zusammen mit Text verloren (User 03.11.2026: „das Bild
 * fehlt ganz").
 */
export function verworfeneBildQuellen(html: string | null | undefined): string[] {
    if (!html) return [];
    const topf = document.createElement("div");
    topf.innerHTML = html;
    const quellen: string[] = [];
    for (const img of Array.from(topf.querySelectorAll("img"))) {
        if (/^\d+$/.test(img.getAttribute("data-bild-id") ?? "")) continue;
        const src = (img.getAttribute("src") ?? "").trim();
        if (BILD_SRC.test(src)) continue;
        quellen.push(src);
    }
    return quellen;
}

/**
 * Aus den verworfenen Bildquellen hochladbare Dateien machen: `data:` und
 * `blob:` lassen sich direkt lesen; was nicht lesbar ist (`file:`, `cid:`),
 * nimmt die nächste noch unbenutzte Bilddatei aus der Zwischenablage. Nie
 * mehr Dateien als verworfene Bilder.
 */
export async function bilderAusQuellen(
    quellen: string[],
    dateien: File[],
    lesen: (src: string) => Promise<Blob> = async (src) => (await fetch(src)).blob(),
): Promise<File[]> {
    const ergebnis: File[] = [];
    let naechste = 0;
    for (const [i, src] of quellen.entries()) {
        let datei: File | null = null;
        if (/^(?:data:image\/|blob:)/i.test(src)) {
            try {
                const blob = await lesen(src);
                if (blob.type.startsWith("image/")) {
                    const endung = blob.type.split("/")[1]?.replace("jpeg", "jpg") ?? "png";
                    datei = new File([blob], `bild-${i + 1}.${endung}`, { type: blob.type });
                }
            } catch {
                datei = null;
            }
        }
        if (!datei && naechste < dateien.length) datei = dateien[naechste++] ?? null;
        if (datei) ergebnis.push(datei);
    }
    return ergebnis;
}

/** Trägt der Transfer überhaupt Dateien? Entscheidet, ob ein Drop angenommen wird. */
export function traegtDateien(dt: DataTransfer | null | undefined): boolean {
    if (!dt) return false;
    if (dt.files?.length) return true;
    return Array.from(dt.types ?? []).includes("Files");
}

/**
 * Der Link unter einem Klick — oder null. Im contentEditable folgt der
 * Browser einem Klick nicht von selbst; und im schreibgeschützten Block
 * folgte er ihm doppelt (nativ UND hier). Deshalb entscheidet immer diese
 * Funktion, und der Aufrufer öffnet den neuen Tab selbst.
 */
export function linkUnter(ziel: EventTarget | null): string | null {
    const a = (ziel as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    const href = a?.getAttribute("href")?.trim();
    return href && /^(?:https?:\/\/|mailto:)/i.test(href) ? href : null;
}

/** Ein Zähler, den der Aufrufer hält — `{ current }`, damit ein `useRef` passt. */
export type UploadZaehler = { current: number };

/**
 * Ein Bild hochladen und einsetzen — der eine Weg für Zwischenablage, Drag &
 * Drop und Palette, im Block wie in der Eingabezeile.
 *
 * Sofort steht ein PLATZHALTER an der Auswahl: man sieht, dass etwas
 * passiert, und der Ort steht fest, bevor der Upload läuft — wer weitertippt
 * oder woanders hinklickt, verschiebt das Bild nicht. Danach wird der
 * Platzhalter zum Marker, und der Marker lädt sein Bild nach. Schlägt der
 * Upload fehl, verschwindet der Platzhalter wieder, und der Nutzer erfährt,
 * warum.
 *
 * `laufend` zählt die offenen Uploads: solange einer läuft, darf der
 * Aufrufer nicht speichern — der Platzhalter ist kein Inhalt, und ein Block,
 * den man mitten im Hochladen verlässt, gälte sonst als leergeräumt.
 *
 * Liefert `true`, wenn das Bild im Feld steht.
 */
export async function bildHochladenUndEinsetzen(
    feld: HTMLElement,
    file: File,
    hochladen: (file: File) => Promise<{ id: number }>,
    opts: { merke?: (url: string) => void; laufend?: UploadZaehler } = {},
): Promise<boolean> {
    const fehler = pruefeNotizbild(file);
    if (fehler) {
        void hinweisen({ titel: "Bild nicht möglich", text: fehler, ton: "danger" });
        return false;
    }
    const platzhalter = bildPlatzhalter(file.name);
    einsetzenAnAuswahl(feld, aktiveAuswahl(), platzhalter);
    if (opts.laufend) opts.laufend.current += 1;
    try {
        const { id } = await hochladen(file);
        ersetzen(platzhalter, bildMarker(id, file.name));
        void hydratisiereBilder(feld, { ...(opts.merke ? { merke: opts.merke } : {}) });
        return true;
    } catch (err) {
        platzhalter.remove();
        console.error("[notizbloecke] Bild hochladen fehlgeschlagen", err);
        void hinweisen({
            titel: "Bild nicht hochgeladen",
            text:
                `Das Bild „${file.name}" konnte nicht hochgeladen werden` +
                (err instanceof Error && err.message ? `: ${err.message}` : "."),
            ton: "danger",
        });
        return false;
    } finally {
        if (opts.laufend) opts.laufend.current -= 1;
    }
}

/**
 * Eingefügtes HTML (aus Outlook, Word, einer Webseite) auf die Auszeichnung
 * der Notizen bringen — T-282, Admin 12.10.2026: „Eingefügte Texte
 * formatierung". Bis dahin lief Strg+V ungefiltert ins Feld; Inline-`style`
 * (Schrift, Größe, weißer Hintergrund) blieb bis zum nächsten Laden stehen,
 * schlug jede Klasse aus der Palette, und nach dem Neuladen sah der Text
 * plötzlich anders aus.
 *
 * Jetzt geht er sofort durch denselben Reiniger wie beim Speichern. Zusätzlich:
 *  · Überschriften, Tabellenzeilen und Absätze werden zu Zeilen (`div`), statt
 *    mit dem Nachbartext zu verschmelzen; Tabellenzellen trennt ein Leerzeichen.
 *  · Klassen bleiben nur, wenn sie zur Palette gehören (`nb-fs-…` usw.) —
 *    `MsoNormal` & Co. sind Ballast, eine kopierte Kommentar-Stelle wäre ein
 *    zweiter Anker auf denselben Faden.
 */
export function bereinigeEingefuegtesHtml(html: string, reinige: (html: string) => string): string {
    const topf = document.createElement("div");
    topf.innerHTML = html;
    // Office-Kommentare, `<style>`-Blöcke und Bedingungen.
    for (const el of Array.from(topf.querySelectorAll("style,script,meta,link,title,xml"))) el.remove();
    // Zellen zuerst zu Text mit Leerzeichen — eine `<td>` außerhalb einer
    // Tabelle verwirft der HTML-Parser beim nächsten Einlesen samt Ordnung.
    const umbenennen = (sel: string, tag: string) => {
        for (const el of Array.from(topf.querySelectorAll(sel))) {
            const neu = document.createElement(tag);
            while (el.firstChild) neu.appendChild(el.firstChild);
            el.replaceWith(neu);
        }
    };
    const aufloesen = (sel: string, danach?: string) => {
        for (const el of Array.from(topf.querySelectorAll(sel))) {
            if (danach) el.appendChild(document.createTextNode(danach));
            el.replaceWith(...Array.from(el.childNodes));
        }
    };
    aufloesen("td,th", " ");
    umbenennen("h1,h2,h3,h4,h5,h6,tr,p,pre,address,section,article,header,footer,caption", "div");
    aufloesen("table,thead,tbody,tfoot,colgroup,col");
    for (const el of Array.from(topf.querySelectorAll("[class]"))) {
        const bleiben = Array.from(el.classList).filter((k) => /^nb-(?:fs|schrift|farbe|mark)-[a-z0-9]+$/.test(k));
        if (bleiben.length) el.setAttribute("class", bleiben.join(" "));
        else el.removeAttribute("class");
    }
    for (const el of Array.from(topf.querySelectorAll("[data-anker],[data-von],[data-am]"))) {
        el.removeAttribute("data-anker");
        el.removeAttribute("data-von");
        el.removeAttribute("data-am");
    }
    return reinige(topf.innerHTML);
}

/**
 * Gereinigtes HTML an der Auswahl einsetzen; der Cursor steht danach hinter
 * dem Eingefügten. Leerer Inhalt (nur Ballast) setzt nichts ein.
 */
export function htmlEinsetzenAnAuswahl(feld: HTMLElement, sel: Selection | null, html: string): boolean {
    const vorlage = document.createElement("template");
    vorlage.innerHTML = html;
    const frag = vorlage.content;
    const letzter = frag.lastChild;
    if (!letzter) return false;
    const r = auswahlImFeld(feld, sel);
    if (r) {
        r.deleteContents();
        r.insertNode(frag);
    } else {
        feld.appendChild(frag);
    }
    if (sel) {
        const nach = aktivesDokument().createRange();
        nach.setStartAfter(letzter);
        nach.collapse(true);
        sel.removeAllRanges();
        sel.addRange(nach);
    }
    return true;
}
